import React from "react";
import { motion } from "framer-motion";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../ui/accordion";

const PoliciesSection = () => {
  return (
    <section id="terms" className="section-spacing bg-slate-900/50" data-testid="policies-section">
      <div className="max-w-4xl mx-auto px-4 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-10"
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Terms & <span className="gradient-text">Conditions</span>
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Key Requirements */}
          <div className="glass rounded-2xl p-6">
            <h3 className="font-bold text-lg mb-4 text-cyan-400">Key Requirements</h3>
            <ul className="space-y-3">
              {[
                "Provide safe, legal parking space",
                "Allow sufficient space around car (approx. 1m)",
                "Provide access to power (if required)",
                "Provide access to water (if required)",
                "Ensure vehicle is unlocked at arrival"
              ].map((item, i) => (
                <li key={i} className="policy-bullet text-slate-300 text-sm">{item}</li>
              ))}
            </ul>
          </div>

          {/* Customer Must */}
          <div className="glass rounded-2xl p-6">
            <h3 className="font-bold text-lg mb-4 text-cyan-400">Before Service</h3>
            <ul className="space-y-3">
              {[
                "Remove all personal belongings from interior & boot",
                "Remove all valuables",
                "Booking deposit required ($20)",
                "24-hour cancellation policy"
              ].map((item, i) => (
                <li key={i} className="policy-bullet text-slate-300 text-sm">{item}</li>
              ))}
            </ul>
            <p className="text-xs text-slate-500 mt-4">
              *We are not responsible for personal items left inside the vehicle
            </p>
          </div>
        </div>

        {/* Accordion for detailed policies */}
        <div className="mt-8">
          <Accordion type="single" collapsible className="glass rounded-2xl overflow-hidden">
            <AccordionItem value="cancellation" className="border-slate-700">
              <AccordionTrigger className="px-6 hover:no-underline text-left">
                Cancellation Policy
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-4 text-slate-400 text-sm">
                <ul className="space-y-2">
                  <li>• 48+ hours: Free reschedule</li>
                  <li>• 24-48 hours: Deposit retained</li>
                  <li>• Under 24 hours: 50% service fee may apply</li>
                </ul>
              </AccordionContent>
            </AccordionItem>
            
            <AccordionItem value="condition" className="border-slate-700">
              <AccordionTrigger className="px-6 hover:no-underline text-left">
                Condition Disclosure
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-4 text-slate-400 text-sm">
                <p className="mb-2">Customers must accurately declare:</p>
                <ul className="space-y-1">
                  <li>• Excessive pet hair, sand/mud buildup</li>
                  <li>• Mold, biohazard, vomit, bodily fluids</li>
                  <li>• Heavy stains, smoke odor</li>
                </ul>
                <p className="mt-2 text-cyan-400 font-medium">Undisclosed heavy conditions may result in additional charges or rescheduling.</p>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="payment" className="border-slate-700">
              <AccordionTrigger className="px-6 hover:no-underline text-left">
                Payment Policy
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-4 text-slate-400 text-sm">
                <ul className="space-y-2">
                  <li>• Payment due immediately after service</li>
                  <li>• Card & cash accepted</li>
                  <li>• No "pay later" options</li>
                  <li>• $20 deposit required to secure booking</li>
                  <li>• <span className="text-yellow-400 font-medium">Deposits are non-refundable</span></li>
                  <li>• <span className="text-cyan-400">Deposits are transferable with 48 hours notice</span></li>
                </ul>
                <p className="mt-4 text-slate-300 bg-slate-800/50 p-3 rounded-lg">
                  <span className="text-cyan-400 font-medium">Unsure about your booking?</span> Text, call or email us and we will respond same day.
                </p>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="weather" className="border-slate-700">
              <AccordionTrigger className="px-6 hover:no-underline text-left">
                Weather & Safety
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-4 text-slate-400 text-sm">
                <p className="mb-2">Service may require rescheduling for safety and quality reasons due to weather.</p>
                <p className="mb-2 text-cyan-400">In case of rain or thunderstorm, we may be able to do service if there is shade that protects from rain.</p>
                <p className="mt-2">We will refuse service if:</p>
                <ul className="space-y-1 mt-2">
                  <li>• Unsafe location</li>
                  <li>• Aggressive behaviour</li>
                  <li>• Hazardous materials</li>
                  <li>• Undeclared biohazard contamination</li>
                </ul>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="satisfaction" className="border-slate-700">
              <AccordionTrigger className="px-6 hover:no-underline text-left">
                Satisfaction Guarantee
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-4 text-slate-400 text-sm">
                <p>If you are not fully satisfied, notify us on site and we will address concerns immediately.</p>
                <p className="mt-3 text-slate-300">
                  We are not responsible for personal items left inside the vehicle.
                </p>
                <p className="mt-2 text-slate-300">
                  We are not responsible for pre-existing damage, worn trim, loose panels or fragile components.
                </p>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="photo-consent" className="border-slate-700 border-b-0">
              <AccordionTrigger className="px-6 hover:no-underline text-left">
                Photo & Media Consent
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-4 text-slate-400 text-sm">
                <p>We may take before and after photos for quality control and marketing.</p>
                <p className="mt-2 text-cyan-400">
                  License plates and personal details will not be displayed without permission.
                </p>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </div>
    </section>
  );
};

export default PoliciesSection;
